import {
  arrayRemove,
  collection,
  deleteDoc,
  deleteField,
  doc,
  FieldPath,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { apiTry } from './api';

// Borrado de agencia EN CASCADA.
//
// El id de la agencia lo escribe el admin a mano (ORYXAGENCY, …) y por tanto se
// REUTILIZA al crear otra. Si solo se borra agencies/{id}, todo lo que cuelga de
// ese id sigue vivo y reaparece en la agencia nueva: creadores importados del
// backstage, misiones, eventos, perfiles, licencias y hasta los roles OWNER_.
// Mismo barrido que hace la app (renderer/tabs/agencias/shared.js).

// Colecciones cuyos documentos llevan el campo agencyId.
export const AGENCY_SCOPED_COLLECTIONS = [
  'agency_members',
  'agency_messages',
  'agency_activity',
  'agency_challenges',
  'agency_events',
  'agency_library_assets',
  'agency_library_folders',
  'agency_manager_assignments',
  'agency_manager_credits',
  'agency_news',
  'agency_overlays',
  'agency_profiles',
  'agency_recommended_actions',
  'agency_training',
  'agency_courses',
  'tiktok_manager_requests'
];

// Borra refs en lotes (el batch de Firestore admite 500 operaciones).
async function deleteRefsInBatches(db, refs) {
  let done = 0;
  for (let i = 0; i < refs.length; i += 400) {
    const slice = refs.slice(i, i + 400);
    const batch = writeBatch(db);
    slice.forEach((ref) => batch.delete(ref));
    await batch.commit();
    done += slice.length;
  }
  return done;
}

// Quita el rol de la agencia (agencyRoles.<id>, lo que miran reglas y
// Worker) y las marcas legacy OWNER_<id> / MANAGER_<id> / SUPEROWNER_<id> de
// los usuarios: si quedan, al recrear la agencia con el mismo id los antiguos
// owners recuperan el acceso.
async function stripAgencyRoles(db, agencyId) {
  const roleOwner = `OWNER_${agencyId}`;
  const roleManager = `MANAGER_${agencyId}`;
  const roleSuperOwner = `SUPEROWNER_${agencyId}`;
  const users = collection(db, 'users');
  const queries = [
    query(users, where(new FieldPath('agencyRoles', agencyId), 'in', ['owner', 'manager'])),
    query(users, where(roleOwner, '==', true)),
    query(users, where(roleManager, '==', true)),
    query(users, where(roleSuperOwner, '==', true)),
    query(users, where('role', 'in', [roleOwner, roleManager, roleSuperOwner])),
    query(users, where('roles', 'array-contains-any', [roleOwner, roleManager, roleSuperOwner]))
  ];

  const found = new Map();
  for (const q of queries) {
    try {
      const snap = await getDocs(q);
      snap.forEach((d) => {
        if (!found.has(d.id)) found.set(d.id, d.data() || {});
      });
    } catch (e) {
      console.warn('[deleteAgency] roles query:', e.message);
    }
  }

  let patched = 0;
  for (const [uid, data] of found) {
    const patch = {
      [`agencyRoles.${agencyId}`]: deleteField(),
      [roleOwner]: deleteField(),
      [roleManager]: deleteField(),
      [roleSuperOwner]: deleteField()
    };
    if (data.role === roleOwner || data.role === roleManager || data.role === roleSuperOwner) patch.role = deleteField();
    if (Array.isArray(data.roles)) patch.roles = arrayRemove(roleOwner, roleManager, roleSuperOwner);
    if (data.agencyId === agencyId) patch.agencyId = deleteField();
    try {
      await updateDoc(doc(db, 'users', uid), patch);
      patched++;
    } catch (e) {
      console.warn('[deleteAgency] roles patch', uid, e.message);
    }
  }
  return patched;
}

/**
 * Borra una agencia y todo lo que cuelga de ella.
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} agencyId
 * @param {(label: string, index: number, total: number) => void} [onProgress]
 * @returns {Promise<{ deleted: Record<string, number>, roles: number, errors: string[] }>}
 */
export async function deleteAgencyCascade(db, agencyId, onProgress) {
  if (!agencyId) return { deleted: {}, roles: 0, errors: ['no_agency_id'] };
  const out = { deleted: {}, roles: 0, errors: [] };
  const total = AGENCY_SCOPED_COLLECTIONS.length + 5;
  let step = 0;
  const tick = (label) => {
    step += 1;
    try {
      onProgress?.(label, step, total);
    } catch (e) {
      /* noop */
    }
  };

  for (const col of AGENCY_SCOPED_COLLECTIONS) {
    tick(col);
    try {
      const snap = await getDocs(query(collection(db, col), where('agencyId', '==', agencyId)));
      const refs = [];
      snap.forEach((d) => refs.push(d.ref));
      out.deleted[col] = await deleteRefsInBatches(db, refs);
    } catch (e) {
      out.errors.push(`${col}: ${e.message}`);
      console.warn('[deleteAgency]', col, e.message);
    }
  }

  // Whitelist (subcolección de entradas + doc contenedor).
  tick('agency_whitelists');
  try {
    const snap = await getDocs(collection(db, 'agency_whitelists', agencyId, 'users'));
    const refs = [];
    snap.forEach((d) => refs.push(d.ref));
    out.deleted.agency_whitelists = await deleteRefsInBatches(db, refs);
    try {
      await deleteDoc(doc(db, 'agency_whitelists', agencyId));
    } catch (e) {
      /* puede no existir como documento */
    }
  } catch (e) {
    out.errors.push(`agency_whitelists: ${e.message}`);
  }

  // Historial del módulo Licencias (subcolección del doc de agencia).
  tick('licenseLogs');
  try {
    const snap = await getDocs(collection(db, 'agencies', agencyId, 'licenseLogs'));
    const refs = [];
    snap.forEach((d) => refs.push(d.ref));
    out.deleted.licenseLogs = await deleteRefsInBatches(db, refs);
  } catch (e) {
    out.errors.push(`licenseLogs: ${e.message}`);
  }

  tick('roles');
  try {
    out.roles = await stripAgencyRoles(db, agencyId);
  } catch (e) {
    out.errors.push(`roles: ${e.message}`);
  }

  // La agencia al final: si algo falla antes, el doc sigue ahí y se puede
  // reintentar en vez de dejar huérfano todo lo demás.
  tick('agencies');
  await deleteDoc(doc(db, 'agencies', agencyId));

  // Lo que no está en Firestore o solo puede tocar el Worker: licencias de
  // sus creadores (reanuda el cobro que pausó la agencia y les devuelve su
  // plan), Backstage y archivos en R2, tracking en D1, colecciones del Worker
  // y KV. Solo actúa con el doc ya borrado (cf-worker ›
  // api/backofficeAgencyPurge.js); repetirlo termina lo que quede.
  tick('limpieza');
  out.purge = await purgeDeletedAgency(agencyId);
  if (out.purge.error) out.errors.push(`limpieza: ${out.purge.error}`);
  else (out.purge.report?.errors || []).forEach((e) => out.errors.push(`limpieza · ${e}`));
  return out;
}

/**
 * Limpieza del Worker de una agencia ya borrada (o restos de una antigua).
 * dryRun: solo cuenta. Nunca lanza → { report } | { error }.
 */
export async function purgeDeletedAgency(agencyId, { dryRun = false } = {}) {
  try {
    const res = await apiTry('/api/backoffice/agencies/purge', { method: 'POST', body: { agencyId, dryRun } });
    if (!res.ok) return { error: (res.data && res.data.error) || `HTTP ${res.status}` };
    return { report: (res.data && res.data.report) || null };
  } catch (e) {
    return { error: e.message };
  }
}

/** Restos de agencias que ya no existen, con lo que se borraría. Lanza si falla. */
export async function listAgencyOrphans() {
  const res = await apiTry('/api/backoffice/agencies/orphans');
  if (!res.ok) throw new Error((res.data && res.data.error) || `HTTP ${res.status}`);
  return (res.data && res.data.orphans) || [];
}

/** Resumen en una línea de un informe de limpieza (para la tabla y los avisos). */
export function purgeSummary(report) {
  if (!report) return '';
  const parts = [];
  const u = report.users || {};
  if (u.licensesRetired) parts.push(`${u.licensesRetired} licencias`);
  if (u.billingResumed) parts.push(`${u.billingResumed} cobros reanudados`);
  if (u.unlinked) parts.push(`${u.unlinked} creadores desvinculados`);
  if (u.notifications) parts.push(`${u.notifications} avisos`);
  const fs = Object.values(report.firestore || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  if (fs) parts.push(`${fs} docs de Firestore`);
  const d1 = report.d1 || {};
  const rows = ['tracked', 'sessions', 'sessionDonors', 'battles', 'boosters', 'events'].reduce((a, k) => a + (Number(d1[k]) || 0), 0);
  if (rows) parts.push(`${rows} filas de tracking`);
  if (report.euler && report.euler.released) parts.push(`${report.euler.released} alertas de Euler`);
  const files = Object.values(report.r2 || {}).reduce((a, r) => a + (Number(r && r.objects) || 0), 0);
  if (files) parts.push(`${files} archivos`);
  if (report.kv && report.kv.keys) parts.push(`${report.kv.keys} claves KV`);
  return parts.join(' · ') || 'nada';
}
